<template>
  <v-container
    fluid
    class="search-area pa-8"
  >
    <SearchFormPanel
      :condition="condition"
      :statuses="daytimeAdviceSheetStatuses"
      :is-searching="isSearching"
      :min-selectable-date="minSelectableDate"
      @update:date="updateConditionField('date', $event)"
      @update:status="updateConditionField('status', $event)"
      @update:doctor-family-name="updateConditionField('doctorFamilyName', $event)"
      @update:doctor-first-name="updateConditionField('doctorFirstName', $event)"
      @search="search"
      @reset="reset"
    />
    <v-row
      v-if="latestRequestToken > 0 && !isSearching"
      class="pl-4 pt-4"
    >
      <v-col
        class="pa-0"
        cols="auto"
      >
        <p class="mb-0">
          {{ count }}件を表示
        </p>
      </v-col>
    </v-row>
    <SearchResultList
      :sheets="sheets"
    />
  </v-container>
</template>

<script>
import {
  clearPersistedState,
  createSearchActions,
  loadPersistedState,
  persistExecutedSearchState
} from "../../../../features/daytime-advice-search";
import { SearchFormPanel, SearchResultList } from "../../../../widgets/daytime-advice-search";

export default {
  name: "DaytimeAdviceSearchPage",
  components: {
    SearchFormPanel,
    SearchResultList
  },
  data() {
    return {
      isSearching: false,
      activeRequestCount: 0,
      latestRequestToken: 0,
      searchActions: null,
      condition: {
        date: null,
        status: null,
        doctorFamilyName: null,
        doctorFirstName: null
      },
      sheets: [],
      daytimeAdviceSheetStatuses: [],
      totalCount: 0,
      count: 0,
      currentDoctor: null
    };
  },
  computed: {
    hasOperatorAccess() {
      const capabilities = this.currentDoctor?.capabilities || [];
      return capabilities.includes("operator:access");
    },
    minSelectableDate() {
      if (this.hasOperatorAccess) {
        return null;
      }

      const minAllowedDay = new Date();
      minAllowedDay.setHours(0, 0, 0, 0);
      minAllowedDay.setMonth(minAllowedDay.getMonth() - 3);

      return minAllowedDay;
    },
  },
  created() {
    this.searchActions = createSearchActions();
    this.initialize();
    this.restoreFromHistoryStateIfNeeded();
  },
  methods: {
    async initialize() {
      const [statuses, currentDoctor] = await Promise.all([
        this.searchActions.fetchStatuses(),
        this.searchActions.fetchCurrentDoctor()
      ]);
      this.daytimeAdviceSheetStatuses = statuses;
      this.currentDoctor = currentDoctor;
    },
    nextRequestToken() {
      this.latestRequestToken += 1;
      return this.latestRequestToken;
    },
    restoreFromHistoryStateIfNeeded() {
      const payload = loadPersistedState();
      if (!payload) return;

      this.condition = {
        date: payload.condition.date ?? null,
        status: payload.condition.status ?? null,
        doctorFamilyName: payload.condition.doctorFamilyName ?? null,
        doctorFirstName: payload.condition.doctorFirstName ?? null
      };
      this.sheets = [];
      const executedCondition = { ...this.condition };
      const token = this.nextRequestToken();
      void this.runWithSearching(async () => {
        const result = await this.searchActions.search(executedCondition);

        if (token !== this.latestRequestToken) {
          return;
        }

        this.applySearchResult(result, executedCondition);
      });
    },
    updateConditionField(field, value) {
      this.condition = this.searchActions.updateCondition(this.condition, field, value);
    },
    applySearchResult(result, executedCondition = this.condition) {
      this.sheets = result.sheets;
      this.totalCount = result.totalCount;
      this.count = result.count;
      persistExecutedSearchState({
        condition: executedCondition
      });
    },
    runWithSearching(task) {
      this.activeRequestCount += 1;
      this.isSearching = true;
      return task().finally(() => {
        this.activeRequestCount = Math.max(0, this.activeRequestCount - 1);
        this.isSearching = this.activeRequestCount > 0;
      });
    },
    search() {
      const executedCondition = { ...this.condition };
      const token = this.nextRequestToken();
      return this.runWithSearching(async () => {
        const result = await this.searchActions.search(executedCondition);

        if (token !== this.latestRequestToken) {
          return;
        }

        this.applySearchResult(result, executedCondition);
      });
    },
    reset() {
      const resetState = this.searchActions.reset();
      this.condition = resetState.condition;
      this.sheets = resetState.sheets;
      this.totalCount = resetState.totalCount;
      this.count = resetState.count;
      clearPersistedState();
    }
  }
};
</script>

<style lang="scss" scoped>
.search-area {
  background-color: rgb(253, 238, 243);
}
</style>
